import { useEffect, useMemo, useRef, useState } from "react";
import Combobox from "../components/Combobox";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import BelegNummerFeld from "../components/invoices/BelegNummerFeld";
import InvoiceAbschlaege from "../components/invoices/InvoiceAbschlaege";
import InvoiceTextKarte from "../components/invoices/InvoiceTextKarte";
import InvoiceWerkzeuge from "../components/invoices/InvoiceWerkzeuge";
import { BelegVersand } from "../components/invoices/BelegVersand";
import FestschreibenFolgen from "../components/invoices/FestschreibenFolgen";
import InvoicePositionEditor from "../components/invoices/InvoicePositionEditor";
import KundeNeuDialog from "../components/kunden/KundeNeuDialog";
import ProjektNeuDialog from "../components/projekt/ProjektNeuDialog";
import DokumentLightbox, { Seitenblatt, type Dokument } from "../components/DokumentLightbox";
import { seitenbilderAus } from "../lib/seitenbilder";
import StrassenFeld from "../components/kunden/StrassenFeld";
import {
  previewInvoicePdf,
  useDeleteWebInvoiceDraft,
  useFinalizeInvoice,
  useInvoiceDocument,
  useInvoiceEditorOptions,
  useInvoiceNumber,
  useSaveWebInvoiceDraft,
  useUpdateFinalizedInvoice,
  useWebInvoiceDraft,
} from "../hooks/useInvoices";
import { useKunden, useProjekte, useUpdateKunde, useProjektAuftragswert } from "../hooks/usePlanung";
import { useNotizProjekt } from "../hooks/useNotizen";
import {
  belegArtLabel,
  errorText,
  euro,
  invoiceDokument,
  type InvoiceFinalizeResult,
  type InvoiceKind,
  type InvoiceRegisterItem,
  type SnippetListe,
  type TextSnippet,
  type WebInvoiceDraft,
} from "../lib/invoices";
import {
  addDays,
  calendarDaysBetween,
  computeWebDraftTotals,
  createInvoiceDraft,
  customerLines,
  anschriftAusKunde,
  joinCustomerLines,
  splitCustomerLines,
  draftProblems,
  normalisiereDraft,
  retextForKind,
  titelVorschlag,
} from "../lib/invoiceEditor";
import type { Anschrift } from "../lib/invoiceEditor";
import type { Kunde, Projekt, ProjektDetail } from "../lib/types";
import "../styles/invoice-editor.css";
import { useNachrichtenBezug } from "../components/nachrichten/NachrichtenKontext";

const kindFromQuery = (value: string | null): InvoiceKind => (
  ["quote", "invoice", "partial_invoice", "final_invoice"].includes(value || "")
    ? value as InvoiceKind
    : "invoice"
);
/**
 * Auswahltexte für Kunde und Projekt.
 *
 * Ohne die interne Nummer — sie sagte niemandem etwas und machte jede Zeile
 * unruhig. Stattdessen der Ort als Unterscheidungshilfe bei Namensgleichheit.
 * Beide Listen werden auf dieselbe Länge gekürzt, damit die Aufklappliste eine
 * ruhige Kante hat statt einer Treppe.
 */
const LABEL_MAX = 46;
const kuerzen = (text: string) => (text.length > LABEL_MAX ? `${text.slice(0, LABEL_MAX - 1)}…` : text);
const customerLabel = (customer: Kunde) => kuerzen(
  [customer.name, (customer.ort || "").trim()].filter(Boolean).join(" · "),
);
const projectLabel = (project: Projekt) => kuerzen(project.title || "Projekt");

export default function RechnungEditorPage() {
  const navigate = useNavigate();
  // Zwei Einstiege, ein Editor: ein Web-Entwurf (`draftId`) oder ein schon
  // festgeschriebener Beleg (`invoiceId`). Im zweiten Fall gibt es keinen
  // Entwurf, also auch kein Autospeichern und kein Verwerfen — gespeichert
  // wird ausdruecklich, und der Server erzeugt das Dokument neu.
  const { draftId: routeDraftId, invoiceId: routeInvoiceId } = useParams();
  const bearbeiten = Boolean(routeInvoiceId);
  const [searchParams] = useSearchParams();
  const initialKind = kindFromQuery(searchParams.get("kind"));
  const [draft, setDraft] = useState<WebInvoiceDraft | null>(null);
  const [activeDraftId, setActiveDraftId] = useState(routeDraftId || "");
  const [customerText, setCustomerText] = useState("");
  /** Dialog „Neuer Kunde" — offen, sobald der Nutzer ihn aus dem Kundenfeld anstößt. */
  const [kundeNeuOffen, setKundeNeuOffen] = useState(false);
  /* Neues Projekt direkt aus dem Beleg (22.09.2026). Kam der Editor aus einer
     Notiz (?notiz=…), hängen Notiz und ihre Besichtigung danach am Projekt —
     Besichtigung → Notiz → Angebot → Projekt ist dann ein Ablauf. */
  const [projektNeuOffen, setProjektNeuOffen] = useState(false);
  const [projektHinweis, setProjektHinweis] = useState("");
  const notizProjekt = useNotizProjekt();
  const [projectText, setProjectText] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewSeiten, setPreviewSeiten] = useState<string[]>([]);
  const [previewSeitenFehler, setPreviewSeitenFehler] = useState("");
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [confirmFinalize, setConfirmFinalize] = useState(false);
  const [finalized, setFinalized] = useState<InvoiceFinalizeResult | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [autoFehler, setAutoFehler] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showProblems, setShowProblems] = useState(false);
  const loadedDraftKey = useRef("");
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autosaveLaeuft = useRef(false);
  const savedSnapshot = useRef("");
  const prefillDone = useRef(false);

  const optionsQuery = useInvoiceEditorOptions();
  const customersQuery = useKunden();
  const projectsQuery = useProjekte(true);
  const savedDraftQuery = useWebInvoiceDraft(routeDraftId);
  const belegQuery = useInvoiceDocument(routeInvoiceId);
  const numberQuery = useInvoiceNumber(draft?.kind || initialKind);
  const saveDraft = useSaveWebInvoiceDraft();
  const finalizeInvoice = useFinalizeInvoice();
  const updateFinalized = useUpdateFinalizedInvoice();
  const deleteDraft = useDeleteWebInvoiceDraft();
  const [aenderungen, setAenderungen] = useState<string[] | null>(null);
  /* Der neu erzeugte Beleg nach „Änderungen übernehmen" — damit er von dort
     aus angesehen und verschickt werden kann (Benjamin, 25.09.2026). */
  const [neuErzeugt, setNeuErzeugt] = useState<InvoiceRegisterItem | null>(null);
  const [neuDok, setNeuDok] = useState<Dokument>(null);
  const customers = customersQuery.data || [];
  const projects = projectsQuery.data || [];

  useEffect(() => {
    if (routeInvoiceId) {
      // Der festgeschriebene Beleg TRAEGT sein Dokument (`web_document_json`).
      // Ohne das gibt es nichts zu bearbeiten — Desktop- und Fremdbelege haben
      // ihre Vorlage woanders; das sagt unten der Ladehinweis.
      if (!belegQuery.data?.web_document_json || loadedDraftKey.current === routeInvoiceId) return;
      const geladen = normalisiereDraft(belegQuery.data.web_document_json);
      loadedDraftKey.current = routeInvoiceId;
      savedSnapshot.current = JSON.stringify(geladen);
      setDraft(geladen);
      setCustomerText(belegQuery.data.kunde_name);
      setProjectText(belegQuery.data.projekt_title);
      return;
    }
    if (routeDraftId) {
      if (!savedDraftQuery.data || loadedDraftKey.current === routeDraftId) return;
      const loaded = normalisiereDraft(savedDraftQuery.data.document);
      loadedDraftKey.current = routeDraftId;
      savedSnapshot.current = JSON.stringify(loaded);
      setDraft(loaded);
      setActiveDraftId(savedDraftQuery.data.id);
      setCustomerText(savedDraftQuery.data.kunde_name);
      setProjectText(savedDraftQuery.data.projekt_title);
      setLastSavedAt(savedDraftQuery.data.updated_at);
      return;
    }
    if (loadedDraftKey.current === "new") return;
    if (!optionsQuery.data || !numberQuery.data) return;
    const created = createInvoiceDraft(
      initialKind,
      numberQuery.data.number,
      numberQuery.data.default_payment_term_days ?? 14,
      optionsQuery.data,
    );
    loadedDraftKey.current = "new";
    savedSnapshot.current = JSON.stringify(created);
    setDraft(created);
    setActiveDraftId("");
    setCustomerText("");
    setProjectText("");
    setLastSavedAt("");
  }, [routeDraftId, routeInvoiceId, savedDraftQuery.data, belegQuery.data, optionsQuery.data, numberQuery.data, initialKind]);

  // Die Nummernvorschau nachziehen — ausser bei einem eingelesenen Beleg. Der
  // bringt die echte Nummer vom Papier mit; sie hier zu ueberschreiben hiesse,
  // dem Kunden ein Dokument mit anderem Namen zu schicken als dem, das er hat.
  useEffect(() => {
    // Ein festgeschriebener Beleg behaelt seine Nummer erst recht — sie ist
    // seine Identitaet, und der Kunde hat sie womoeglich schon.
    if (bearbeiten) return;
    if (!draft || draft.nummer_uebernehmen) return;
    if (!numberQuery.data?.number || draft.doc_number === numberQuery.data.number) return;
    setDraft((current) => current ? { ...current, doc_number: numberQuery.data!.number } : current);
  }, [draft?.kind, draft?.nummer_uebernehmen, numberQuery.data?.number]); // eslint-disable-line react-hooks/exhaustive-deps

  // Aus Projekt oder Kunde heraus angelegt: /rechnungen/neu?kind=…&kunde=…&projekt=…
  // Genau einmal anwenden, damit ein spaeter geaenderter Kunde nicht zurueckspringt.
  useEffect(() => {
    if (routeDraftId || !draft || prefillDone.current) return;
    const wantedCustomer = searchParams.get("kunde") || "";
    const wantedProject = searchParams.get("projekt") || "";
    if (!wantedCustomer && !wantedProject) { prefillDone.current = true; return; }
    if (wantedCustomer && !customers.length) return;
    if (wantedProject && !projects.length) return;
    const customer = customers.find((item) => item.id === wantedCustomer);
    const project = projects.find((item) => item.id === wantedProject);
    prefillDone.current = true;
    setDraft((current) => current ? {
      ...current,
      customer_number: customer?.id || current.customer_number,
      customer_lines: customer ? customerLines(customer) : current.customer_lines,
      source_project_id: project?.id || current.source_project_id,
      doc_title: (current.doc_title || "") || titelVorschlag(current.kind, project?.title || ""),
    } : current);
    if (customer) setCustomerText(customerLabel(customer));
    if (project) setProjectText(projectLabel(project));
  }, [routeDraftId, draft, searchParams, customers, projects]);

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const totals = useMemo(() => draft ? computeWebDraftTotals(draft) : null, [draft]);
  const problems = useMemo(() => draft ? draftProblems(draft) : [], [draft]);
  const isDirty = Boolean(draft) && JSON.stringify(draft) !== savedSnapshot.current;
  const mutationError = saveDraft.error || finalizeInvoice.error || updateFinalized.error;
  const editorError = previewError || (mutationError ? errorText(mutationError) : "");

  /**
   * Woran man sieht, dass der Beleg schon nach außen wirkt.
   *
   * Nur die harten Spuren — bezahlt, an einer Bankbuchung, aus Kontor
   * versendet. Ein Versand aus einem anderen Postfach hinterlässt hier nichts;
   * deshalb ist das ein Hinweis und kein Urteil.
   */
  const herausgabeHinweis = useMemo(() => {
    const beleg = belegQuery.data;
    if (!beleg) return "";
    const gruende: string[] = [];
    if (beleg.bezahlt_am) gruende.push("als bezahlt vermerkt");
    if (beleg.kontobewegung_id) gruende.push("einer Bankbuchung zugeordnet");
    if ((beleg.aktivitaet || []).some((eintrag) => eintrag.typ === "versand")) gruende.push("aus Kontor versendet");
    return gruende.join(", ");
  }, [belegQuery.data]);

  /**
   * Erst speichern, wenn wirklich etwas drinsteht.
   *
   * Ohne diese Schwelle legte jedes geoeffnete Formular sofort einen Entwurf an
   * und das Register liefe mit leeren Belegen voll.
   */
  const beruehrt = Boolean(draft) && (
    Boolean(draft?.customer_number)
    || (draft?.line_rows || []).some((zeile) => (zeile.title || "").trim().length > 0)
  );

  /**
   * Autospeichern: 2,5 s nach der letzten Aenderung.
   *
   * Der erste Durchlauf legt den Entwurf an und schreibt seine Adresse in die
   * Leiste — ab dann ueberlebt die Arbeit auch ein geschlossenes Fenster. Der
   * beforeunload-Waechter bleibt als Netz fuer die Sekunden dazwischen.
   */
  useEffect(() => {
    // Im Bearbeiten-Modus gibt es keinen Entwurf, in den man still speichern
    // koennte — und stilles Speichern waere hier auch falsch: jede Aenderung
    // erzeugt ein neues PDF und einen Verlaufseintrag. Das geschieht auf
    // Knopfdruck, nicht nebenbei.
    if (bearbeiten) return;
    if (!draft || !isDirty || !beruehrt) return;
    if (finalizeInvoice.isPending || finalized) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => { void autosave(); }, 2500);
    return () => { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); };
  }, [draft, isDirty, beruehrt, finalizeInvoice.isPending, finalized]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const warnBeforeClose = (event: BeforeUnloadEvent) => {
      if (!isDirty) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeClose);
    return () => window.removeEventListener("beforeunload", warnBeforeClose);
  }, [isDirty]);

  const gewaehlterKunde = customers.find((item) => item.id === draft?.customer_number);
  // Das Nachrichtenfenster hebt die Gespräche dieses Kunden und Projekts nach oben.
  useNachrichtenBezug({ kundeId: draft?.customer_number, kundeName: gewaehlterKunde?.name, projektId: draft?.source_project_id });

  /**
   * Die Anschrift lebt im Dokument als mehrzeiliger Text (so will sie die
   * PDF-Engine), in der Maske aber als Einzelfelder.
   *
   * Sie ist **eigener State** und wird nicht bei jedem Tastendruck aus dem Text
   * zurückgelesen: eine halb getippte Postleitzahl („0") erfüllt das PLZ-Muster
   * noch nicht, landete beim Zurücklesen im Straßenfeld — und die Eingabe hing
   * scheinbar im vorherigen Feld fest. Gelesen wird nur, wenn das Dokument von
   * außen wechselt (Laden, Kundenwahl).
   */
  const [anschrift, setAnschriftState] = useState<Anschrift>({ name: "", strasse: "", plz: "", ort: "" });
  const anschriftQuelle = useRef("");
  useEffect(() => {
    const text = draft?.customer_lines || "";
    if (text === anschriftQuelle.current) return;
    anschriftQuelle.current = text;
    setAnschriftState(splitCustomerLines(text));
  }, [draft?.customer_lines]);
  const setAnschrift = (neu: Anschrift) => {
    if (!draft) return;
    setAnschriftState(neu);
    const text = joinCustomerLines(neu);
    anschriftQuelle.current = text;
    setDraft({ ...draft, customer_lines: text });
  };

  const chooseCustomer = (value: string) => {
    setCustomerText(value);
    if (!draft) return;
    const customer = customers.find((item) => customerLabel(item) === value || item.name.toLowerCase() === value.toLowerCase());
    setDraft({
      ...draft,
      customer_number: customer?.id || "",
      customer_lines: customer ? customerLines(customer) : draft.customer_lines,
    });
  };

  /** Ein eben angelegter Kunde steht noch nicht in `customers` — direkt setzen. */
  const kundeUebernehmen = (kunde: Kunde) => {
    setKundeNeuOffen(false);
    setCustomerText(customerLabel(kunde));
    if (!draft) return;
    setDraft({ ...draft, customer_number: kunde.id, customer_lines: customerLines(kunde) });
  };

  const projektAngelegt = (projekt: ProjektDetail) => {
    setProjektNeuOffen(false);
    setProjectText(kuerzen(projekt.title || "Projekt"));
    if (draft) {
      const kunde = customers.find((item) => item.id === projekt.kunde_id);
      setDraft({
        ...draft,
        source_project_id: projekt.id,
        ...(draft.customer_number || !kunde ? {} : { customer_number: kunde.id, customer_lines: customerLines(kunde) }),
      });
      if (!draft.customer_number && kunde) setCustomerText(customerLabel(kunde));
    }
    const notizId = searchParams.get("notiz") || "";
    if (!notizId) { setProjektHinweis(`Projekt „${projekt.title}" angelegt.`); return; }
    notizProjekt.mutate({ id: notizId, projektId: projekt.id }, {
      onSuccess: (r) => setProjektHinweis(`Projekt „${projekt.title}" angelegt — Notiz${r.termin_verknuepft ? " und Besichtigung gehören" : " gehört"} jetzt dazu.`),
      onError: () => setProjektHinweis(`Projekt „${projekt.title}" angelegt; die Notiz ließ sich nicht zuordnen.`),
    });
  };

  const chooseProject = (id: string) => {
    const project = projects.find((item) => item.id === id);
    setProjectText(project ? projectLabel(project) : "");
    if (!draft) return;
    setDraft({ ...draft, source_project_id: id });
  };

  const changeKind = (kind: InvoiceKind) => {
    if (!draft) return;
    const isQuote = kind === "quote";
    const paymentDays = draft.payment_term_days ?? numberQuery.data?.default_payment_term_days ?? 14;
    setDraft({
      ...draft,
      kind,
      due_date: isQuote ? null : (draft.due_date || addDays(draft.issue_date, paymentDays)),
      service_date: isQuote ? null : (draft.service_date || draft.issue_date),
      validity_until: isQuote ? (draft.validity_until || addDays(draft.issue_date, 30)) : null,
      payment_term_days: isQuote ? null : paymentDays,
      source_quote_number: isQuote ? "" : draft.source_quote_number,
      zugferd: !isQuote && draft.zugferd,
      xml_out: !isQuote && draft.xml_out,
      abschlag_absetzungen: kind === "final_invoice" ? draft.abschlag_absetzungen : [],
      ...retextForKind(draft, kind, optionsQuery.data),
    });
  };

  /**
   * Ausstellung, Zahlungsziel und Fälligkeit hängen zusammen — wie im Desktop.
   * Wer die Fälligkeit direkt setzt, verschiebt damit auch das Zahlungsziel;
   * sonst stünde im Fußtext eine Tageszahl, die nicht zum Datum passt.
   */
  const setIssueDate = (issue: string) => {
    if (!draft || !issue) return;
    const isQuote = draft.kind === "quote";
    const days = draft.payment_term_days ?? 14;
    setDraft({
      ...draft,
      issue_date: issue,
      service_date: isQuote ? null : (draft.service_date || issue),
      due_date: isQuote ? null : addDays(issue, days),
    });
  };

  const setDueDate = (due: string) => {
    if (!draft) return;
    if (!due) { setDraft({ ...draft, due_date: null }); return; }
    setDraft({ ...draft, due_date: due, payment_term_days: calendarDaysBetween(draft.issue_date, due) });
  };

  const setPaymentTermDays = (raw: string) => {
    if (!draft) return;
    const days = Math.max(0, Math.min(365, Number(raw) || 0));
    setDraft({ ...draft, payment_term_days: days, due_date: addDays(draft.issue_date, days) });
  };

  const speichern = async (still: boolean) => {
    if (!draft || autosaveLaeuft.current) return;
    autosaveLaeuft.current = true;
    if (!still) setPreviewError("");
    try {
      const record = await saveDraft.mutateAsync({ id: activeDraftId || undefined, draft });
      savedSnapshot.current = JSON.stringify(record.document);
      loadedDraftKey.current = record.id;
      /* Beim stillen Speichern NICHT das Dokument aus der Antwort zurueckschreiben:
         der Nutzer tippt in diesem Moment weiter, und ein setDraft wuerde ihm die
         Zeichen unter den Fingern wegnehmen. Der Schnappschuss genuegt. */
      if (!still) {
        setDraft(record.document);
        setCustomerText(record.kunde_name);
        setProjectText(record.projekt_title);
      }
      setActiveDraftId(record.id);
      setLastSavedAt(record.updated_at);
      setAutoFehler(false);
      if (!routeDraftId) navigate(`/rechnungen/entwurf/${record.id}`, { replace: true });
    } catch {
      /* Beim Knopfdruck traegt die Mutation die Meldung. Im Hintergrund bleibt es
         still — aber sichtbar, damit niemand auf ein Speichern vertraut, das
         nicht stattgefunden hat. */
      if (still) setAutoFehler(true);
    } finally {
      autosaveLaeuft.current = false;
    }
  };
  const autosave = () => speichern(true);

  const removeDraft = async () => {
    if (!activeDraftId) return;
    try {
      await deleteDraft.mutateAsync(activeDraftId);
      navigate("/rechnungen", { replace: true });
    } catch (error) {
      setPreviewError(errorText(error));
      setConfirmDelete(false);
    }
  };

  const preview = async () => {
    if (!draft) return;
    if (problems.length) { setShowProblems(true); return; }
    setPreviewOpen(true);
    setPreviewBusy(true);
    setPreviewError("");
    try {
      // Der Server druckt in die Vorschau die naechste freie Nummer — ausser
      // der Entwurf beansprucht ausdruecklich seine eigene. Ein
      // festgeschriebener Beleg HAT seine; ohne dieses Flag stuende auf dem
      // Blatt, das man prueft, eine andere Nummer als auf dem echten Dokument.
      // Gespeichert wird das Flag nicht — beim Uebernehmen setzt der Server es
      // ohnehin zurueck.
      const result = await previewInvoicePdf(bearbeiten ? { ...draft, nummer_uebernehmen: true } : draft);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(result.blob));
      // Gezeigt wird die Vorschau als Seitenbilder, nicht als eingebettetes
      // PDF — das zeigte das Telefon nicht (siehe DokumentLightbox).
      setPreviewSeiten([]);
      setPreviewSeitenFehler("");
      try {
        setPreviewSeiten(await seitenbilderAus(result.blob));
      } catch (fehler) {
        setPreviewSeitenFehler(errorText(fehler));
      }
      // Die Vorschau rendert mit der naechsten freien Nummer. Ein eingelesener
      // und erst recht ein festgeschriebener Beleg behaelt trotzdem seine
      // eigene — sonst nimmt ein Blick in die Vorschau ihm still die Nummer weg.
      if (!draft.nummer_uebernehmen && !bearbeiten) setDraft({ ...draft, doc_number: result.number });
    } catch (error) {
      setPreviewError(errorText(error));
    } finally {
      setPreviewBusy(false);
    }
  };

  const finalize = async () => {
    if (!draft) return;
    setPreviewError("");
    try {
      const result = await finalizeInvoice.mutateAsync({ draftId: activeDraftId || undefined, draft });
      setFinalized(result);
      setConfirmFinalize(false);
    } catch { /* Mutation enthält die Meldung. */ }
  };

  /**
   * Den festgeschriebenen Beleg mit dem geänderten Inhalt neu erzeugen.
   *
   * Die Nummer bleibt, PDF und ZUGFeRD-XML entstehen neu, und der Server
   * schreibt „Rechnung geändert — <Felder>" in den Verlauf. Was er dort
   * festgehalten hat, kommt zurück und steht hier als Bestätigung — sonst
   * bliebe offen, ob überhaupt etwas angekommen ist.
   */
  const uebernehmen = async () => {
    if (!draft || !routeInvoiceId) return;
    setPreviewError("");
    try {
      const result = await updateFinalized.mutateAsync({ id: routeInvoiceId, draft });
      savedSnapshot.current = JSON.stringify(draft);
      setAenderungen(result.aenderungen || []);
      setNeuErzeugt(result);
      setConfirmFinalize(false);
    } catch { /* Mutation enthält die Meldung. */ }
  };

  /**
   * Dieselbe Entscheidung wie im Festschreiben-Knopf oben — einmal notiert,
   * damit die kompakte Kopie in der klebenden Positionen-Leiste (Benjamin,
   * 01.09.2026: „damit der Nutzer sie ebenfalls immer erreicht") nicht ein
   * zweites Mal dieselbe Logik trägt.
   */
  const onFestschreibenKlick = () => {
    if (problems.length) { setShowProblems(true); return; }
    if (bearbeiten) { void uebernehmen(); return; }
    setConfirmFinalize(true);
  };

  const leaveEditor = () => {
    if (isDirty && !window.confirm("Ungespeicherte Änderungen verwerfen und zum Register zurückkehren?")) return;
    navigate("/rechnungen");
  };

  if (!draft) {
    const failed = savedDraftQuery.isError || optionsQuery.isError || numberQuery.isError || belegQuery.isError;
    // Ein Beleg ohne `web_document_json` kommt aus der Desktop-App oder einem
    // fremden Programm. Er lässt sich nicht neu erzeugen — das gehört gesagt,
    // sonst wartet man auf einen Editor, der nie kommt.
    if (bearbeiten && belegQuery.data && !belegQuery.data.web_document_json) {
      return (
        <div className="invoice-editor-loading">
          {belegQuery.data.nummer} wurde nicht in Kontor geschrieben — es gibt kein Dokument,
          das sich hier ändern ließe. Seine Vorlage liegt in dem Programm, aus dem er stammt.
        </div>
      );
    }
    return <div className="invoice-editor-loading">{failed ? "Der Dokumenteditor konnte nicht geladen werden. Bitte die Seite erneut öffnen." : "Dokumenteditor wird vorbereitet …"}</div>;
  }

  return (
    <div className="invoice-editor-page">
      <header className="invoice-editor-head">
        <button className="invoice-editor-back" type="button" onClick={leaveEditor}>← Zum Register</button>
        <div className="invoice-editor-title">
          <p className="invoice-kicker">{bearbeiten ? "Festgeschriebener Beleg" : "Dokumenteditor"}</p>
          <h1>
            {bearbeiten
              ? `${belegArtLabel(draft.kind, draft.source_quote_number)} ${draft.doc_number} ändern`
              : activeDraftId ? `${belegArtLabel(draft.kind, draft.source_quote_number)} bearbeiten` : `${belegArtLabel(draft.kind, draft.source_quote_number)} erstellen`}
          </h1>
          <span>
            {bearbeiten
              ? "Die Nummer bleibt. Beim Übernehmen werden PDF und E-Rechnung neu erzeugt und die Änderung im Verlauf festgehalten."
              : "Vorschau und PDF verwenden exakt dieselbe Vorlage wie die Desktop-App."}
          </span>
          {/* Der Speicherstand steht nur noch in der Fußleiste. Im Kopf wechselte
              der Satz mit jedem Tastendruck seine Länge und schob das Layout hin
              und her — Autospeichern soll man nicht sehen, sondern haben. */}
        </div>
        <div className="invoice-editor-head-actions">
          {/* Der Knopf ist zurück (Benjamin, 16.09.2026): automatisch gespeichert
              zu werden ersetzt nicht das Bedürfnis, es einmal selbst auszulösen —
              gerade bevor man den Rechner zuklappt.
              Warum er am 02.09. verschwand, bleibt trotzdem gültig: sein Text
              wechselte zwischen „Speichert …“ und „Änderungen speichern“ und
              verschob damit bei jedem Autospeichern die ganze Kopfzeile. Deshalb
              hat er jetzt einen **festen** Text und wird während des Speicherns
              nur gesperrt; der Stand steht weiterhin in der Fußleiste.
              Im Bearbeiten-Modus gibt es keinen Entwurf, in den man speichern
              könnte — dort bleibt er weg. */}
          {!bearbeiten && (
            <button
              className="invoice-button"
              type="button"
              onClick={() => { void speichern(false); }}
              disabled={saveDraft.isPending}
            >
              Entwurf speichern
            </button>
          )}
          <button className="invoice-button" type="button" onClick={preview} disabled={previewBusy}>{previewBusy ? "Erzeugt PDF …" : "PDF-Vorschau"}</button>
          <button
            className="invoice-button primary"
            type="button"
            onClick={onFestschreibenKlick}
            disabled={bearbeiten && (!isDirty || updateFinalized.isPending)}
          >
            {bearbeiten
              ? (updateFinalized.isPending ? "Wird übernommen …" : "Änderungen übernehmen")
              : "Festschreiben"}
          </button>
          {activeDraftId && !bearbeiten && (
            <button className="invoice-button secondary" type="button" onClick={() => setConfirmDelete(true)} disabled={deleteDraft.isPending}>
              Entwurf verwerfen
            </button>
          )}
        </div>
      </header>

      {/* Kein Riegel, sondern ein Blick über die Schulter: Kontor kann nicht
          wissen, ob du die Rechnung aus deinem eigenen Postfach verschickt
          hast — aber wo es etwas weiß, sagt es das, bevor du änderst. */}
      {bearbeiten && herausgabeHinweis && (
        <div className="invoice-editor-warnung">
          <strong>Dieser Beleg wirkt bereits nach außen: {herausgabeHinweis}</strong>
          <span>
            Änderungen sind trotzdem möglich und werden protokolliert. Weicht das Papier beim
            Kunden danach vom Register ab, ist der saubere Weg ein Storno mit neuer Rechnung.
          </span>
        </div>
      )}
      {editorError && <div className="invoice-editor-error" role="alert">{editorError}</div>}
      {showProblems && problems.length > 0 && (
        <div className="invoice-editor-problems" role="alert">
          <strong>Das fehlt noch, bevor der Beleg rausgehen kann:</strong>
          <ul>{problems.map((problem) => <li key={problem}>{problem}</li>)}</ul>
        </div>
      )}

      <div className="invoice-editor-layout">
        <main className="invoice-editor-form">
          <Klappkarte
            kicker="Grunddaten"
            titel="Dokument & Empfänger"
            rechts={draft.nummer_uebernehmen
              ? `Nr. ${draft.doc_number || "wird gezogen"} · vom Beleg`
              : `Nächste Nr. ${draft.doc_number || "wird ermittelt"}`}
            zusammenfassung={`${anschrift.name || "ohne Empfänger"}${projectText ? ` · ${projectText}` : ""} · ${draft.issue_date}`}
            standardOffen={!activeDraftId}
          >
            {/* Zwei Spalten: links wer und was, rechts alle Daten. Vorher lief
                alles durch ein Drei-Spalten-Raster, wodurch die Termine unter
                dem Betreff verstreut standen und die Karte unnötig hoch wurde. */}
            <div className="invoice-editor-grunddaten">
              <div className="invoice-editor-spalte">
                <label className="invoice-editor-field"><span>Belegart</span><select value={draft.kind} onChange={(event) => changeKind(event.target.value as InvoiceKind)}><option value="invoice">Rechnung</option><option value="partial_invoice">Abschlagsrechnung</option><option value="final_invoice">Schlussrechnung</option><option value="quote">Angebot</option></select></label>
                <label className="invoice-editor-field invoice-kunde-feld">
                  <span>
                    Kunde *
                    <button type="button" className="invoice-nummer-schalter" onClick={() => setKundeNeuOffen(true)}>+ Neuer Kunde</button>
                  </span>
                  <input list="invoice-editor-customers" value={customerText} onChange={(event) => chooseCustomer(event.target.value)} placeholder="Kunde suchen …" />
                  <datalist id="invoice-editor-customers">{customers.map((customer) => <option value={customerLabel(customer)} key={customer.id} />)}</datalist>
                  {/* Nichts gefunden: der getippte Name wird zum Vorschlag für den neuen Kunden. */}
                  {customerText.trim() && !draft.customer_number && (
                    <p className="invoice-anschrift-hinweis">
                      Kein Kunde gewählt.{" "}
                      <button type="button" className="invoice-linkbutton" onClick={() => setKundeNeuOffen(true)}>
                        „{customerText.trim()}" als neuen Kunden anlegen?
                      </button>
                    </p>
                  )}
                </label>
                <AnschriftFelder
                  anschrift={anschrift}
                  onChange={setAnschrift}
                  kunde={gewaehlterKunde}
                />
              </div>

              <div className="invoice-editor-spalte is-daten">
                <BelegNummerFeld
                  draft={draft}
                  vorschlag={numberQuery.data?.number || ""}
                  onChange={setDraft}
                  festgeschrieben={bearbeiten}
                />
                {/* Pflichtfeld seit 16.09.2026 — und deshalb endlich eine
                    Auswahl, die man bedienen kann: die alte `datalist` filterte
                    nur auf den ganzen Optionstext und lieferte fast nie eine ID. */}
                <div className="invoice-editor-field">
                  <span>
                    Projekt *
                    <button type="button" className="invoice-nummer-schalter" onClick={() => setProjektNeuOffen(true)}>+ Neues Projekt</button>
                  </span>
                  <Combobox
                    wert={draft.source_project_id}
                    eintraege={projects.map((project) => ({ id: project.id, label: project.title, zusatz: project.kunde_name || "ohne Kunde" }))}
                    onWaehlen={chooseProject}
                    platzhalter="Projekt suchen …"
                    ariaLabel="Projekt des Belegs"
                  />
                  {projektHinweis && <p className="invoice-anschrift-hinweis">{projektHinweis}</p>}
                </div>
                <label className="invoice-editor-field"><span>Ausstellung</span><input type="date" value={draft.issue_date} onChange={(event) => setIssueDate(event.target.value)} /></label>
                {draft.kind === "quote" ? (
                  <label className="invoice-editor-field"><span>{draft.source_quote_number ? `Bestätigen bis * (zu Angebot ${draft.source_quote_number})` : "Gültig bis *"}</span><input type="date" value={draft.validity_until || ""} onChange={(event) => setDraft({ ...draft, validity_until: event.target.value || null })} /></label>
                ) : (
                  <>
                    <label className="invoice-editor-field"><span>Leistungsdatum</span><input type="date" value={draft.service_date || ""} onChange={(event) => setDraft({ ...draft, service_date: event.target.value || null })} /></label>
                    <label className="invoice-editor-field"><span>Zahlungsziel (Tage)</span><input type="number" min="0" max="365" value={draft.payment_term_days ?? ""} onChange={(event) => setPaymentTermDays(event.target.value)} /></label>
                    <label className="invoice-editor-field"><span>Fällig am *</span><input type="date" value={draft.due_date || ""} onChange={(event) => setDueDate(event.target.value)} /></label>
                    <label className="invoice-editor-field"><span>Angebots-Nr.</span><input value={draft.source_quote_number} onChange={(event) => setDraft({ ...draft, source_quote_number: event.target.value })} placeholder="optional" /></label>
                  </>
                )}
                <label className="invoice-editor-field"><span>Rabatt in %</span><input type="number" min="0" max="100" step="0.01" value={draft.discount_percent ?? ""} onChange={(event) => setDraft({ ...draft, discount_percent: event.target.value === "" ? null : Number(event.target.value) })} placeholder="optional" /></label>
              </div>
            </div>
          </Klappkarte>

          <TextAreaCard liste={draft.kind === "quote" ? "intro_quote" : "intro_invoice"} standardOffen={!activeDraftId} title="Titel & Einleitung" kicker="Anschreiben" vorspann={<TitelZeile draft={draft} onChange={(doc_title) => setDraft({ ...draft, doc_title })} />} zusammenfassung={(draft.doc_title || "").trim()} value={draft.intro_paragraphs} snippets={draft.kind === "quote" ? optionsQuery.data?.snippets.intro_quote || [] : optionsQuery.data?.snippets.intro_invoice || []} onChange={(value) => setDraft({ ...draft, intro_paragraphs: value })} draft={draft} />
          <InvoicePositionEditor
            draft={draft}
            units={optionsQuery.data?.units || []}
            entwurfId={activeDraftId}
            rechnungId={routeInvoiceId || ""}
            onChange={setDraft}
            onPreview={preview}
            previewBusy={previewBusy}
            onFinalize={onFestschreibenKlick}
            finalizeLabel={bearbeiten
              ? (updateFinalized.isPending ? "Wird übernommen …" : "Änderungen übernehmen")
              : "Festschreiben"}
            finalizeDisabled={bearbeiten && (!isDirty || updateFinalized.isPending)}
            onDiscard={activeDraftId && !bearbeiten ? () => setConfirmDelete(true) : undefined}
          />
          {draft.kind === "final_invoice" && <InvoiceAbschlaege draft={draft} onChange={setDraft} />}
          {/* Vorlagen und Kalkulation: zwei Reiter, eingeklappt bis man sie braucht. */}
          <InvoiceWerkzeuge draft={draft} onChange={setDraft} />
          <TextAreaCard liste={draft.kind === "quote" ? "notes_quote" : "notes_invoice"} standardOffen={!activeDraftId} title="Text nach der Tabelle" kicker="Zahlung & Hinweise" value={draft.notes_after_table} snippets={draft.kind === "quote" ? optionsQuery.data?.snippets.notes_quote || [] : optionsQuery.data?.snippets.notes_invoice || []} onChange={(value) => setDraft({ ...draft, notes_after_table: value })} draft={draft} />
          {/* „PDF & E-Rechnung" stand hier als eigene Karte; die Wahl gehört zum
              Festschreiben und steht seit 24.09.2026 im Festschreiben-Dialog. */}
        </main>

      </div>

      {previewOpen && (
        <PdfVorschau
          url={previewUrl}
          seiten={previewSeiten}
          seitenFehler={previewSeitenFehler}
          busy={previewBusy}
          onNeu={preview}
          onClose={() => setPreviewOpen(false)}
        />
      )}

      {totals && (
        <div className="invoice-editor-bar">
          <div className="invoice-editor-bar-sums">
            <span><small>Netto</small><strong>{euro(totals.net)}</strong></span>
            <span><small>USt.</small><strong>{euro(totals.vat)}</strong></span>
            <span className="grand"><small>Gesamt</small><strong>{euro(totals.gross)}</strong></span>
            {/* Nur wenn es Optionale gibt, und bewusst NACH dem Gesamtbetrag:
                die Angebotssumme ohne Optionen bleibt die fette Zahl. */}
            {totals.optional > 0 && (
              <span className="optional"><small>Optionen (netto)</small><strong>{euro(totals.optional)}</strong></span>
            )}
          </div>
          {/* Genau zwei Zustände und feste Breite: der Text darf beim Tippen
              nicht die Zeile umbrechen lassen. */}
          <span className={`invoice-editor-autosave${autoFehler ? " is-error" : ""}`} aria-live="polite">
            {autoFehler
              ? "Speichern fehlgeschlagen"
              : lastSavedAt
                ? `Gespeichert · ${new Date(lastSavedAt).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}`
                : "Speichert automatisch"}
          </span>
        </div>
      )}

      {confirmFinalize && (
        <FinalizeDialog
          draft={draft}
          total={totals?.gross || 0}
          busy={finalizeInvoice.isPending}
          onClose={() => setConfirmFinalize(false)}
          onConfirm={finalize}
          onKind={changeKind}
          onZugferd={(an) => setDraft({ ...draft, zugferd: an, xml_out: an })}
        />
      )}
      {finalized && (
        <FinalizedDialog
          result={finalized}
          onRegister={() => navigate("/rechnungen")}
          onBearbeiten={() => { const id = finalized.id; setFinalized(null); navigate(`/rechnungen/beleg/${id}/bearbeiten`); }}
        />
      )}
      {aenderungen && (
        <div className="invoice-modal-backdrop">
          <section className="invoice-final-dialog" role="dialog" aria-modal="true">
            <p className="invoice-kicker">Übernommen</p>
            <h2>{draft.doc_number} wurde neu erzeugt</h2>
            {aenderungen.length > 0 ? (
              <>
                <p>Das steht ab jetzt im Verlauf des Belegs:</p>
                <ul className="invoice-aenderungsliste">
                  {aenderungen.map((eintrag) => <li key={eintrag}>{eintrag}</li>)}
                </ul>
              </>
            ) : <p>Die Änderung ist gespeichert.</p>}
            {neuErzeugt && <Versand item={neuErzeugt} />}
            <div className="invoice-modal-actions">
              <button className="invoice-button" type="button" onClick={() => setAenderungen(null)}>Weiter bearbeiten</button>
              <button className="invoice-button" type="button" onClick={() => navigate("/rechnungen")}>Zum Register</button>
              {neuErzeugt && (
                <button className="invoice-button primary" type="button" onClick={() => setNeuDok(invoiceDokument(neuErzeugt))}>Ansehen</button>
              )}
            </div>
          </section>
          <DokumentLightbox dok={neuDok} onClose={() => setNeuDok(null)} />
        </div>
      )}
      {confirmDelete && (
        <div className="invoice-modal-backdrop">
          <section className="invoice-final-dialog" role="dialog" aria-modal="true">
            <p className="invoice-kicker">Entwurf verwerfen</p>
            <h2>{belegArtLabel(draft.kind, draft.source_quote_number)}-Entwurf löschen?</h2>
            <p>Der Entwurf verschwindet aus dem Register. Eine Belegnummer wurde noch nicht vergeben, es entsteht also keine Lücke.</p>
            <div className="invoice-modal-actions">
              <button className="invoice-button" type="button" onClick={() => setConfirmDelete(false)} disabled={deleteDraft.isPending}>Behalten</button>
              <button className="invoice-button primary" type="button" onClick={removeDraft} disabled={deleteDraft.isPending}>
                {deleteDraft.isPending ? "Löscht …" : "Endgültig löschen"}
              </button>
            </div>
          </section>
        </div>
      )}
      {projektNeuOffen && (
        <ProjektNeuDialog
          kundeId={draft?.customer_number || ""}
          vorschlagTitel={searchParams.get("titel") || ""}
          hinweis={searchParams.get("notiz") ? "wird gleich in den Beleg übernommen — Notiz und Besichtigung hängen danach am Projekt" : "wird gleich in den Beleg übernommen"}
          onSchliessen={() => setProjektNeuOffen(false)}
          onAngelegt={projektAngelegt}
        />
      )}
      {kundeNeuOffen && (
        <KundeNeuDialog
          vorschlagName={draft.customer_number ? "" : customerText}
          onAngelegt={kundeUebernehmen}
          onSchliessen={() => setKundeNeuOffen(false)}
        />
      )}
    </div>
  );
}

/**
 * PDF-Vorschau über die ganze Fläche.
 *
 * Als schmale Spalte war das Dokument zu klein, um es zu beurteilen. Seit
 * 24.09.2026 erscheint es als Seitenbilder wie in der Dokumentansicht — ein
 * eingebettetes PDF zeigte das iPhone nur mit der ersten Seite und Android gar
 * nicht. „PDF" öffnet die echte Datei.
 */
function PdfVorschau({ url, seiten, seitenFehler, busy, onNeu, onClose }: {
  url: string; seiten: string[]; seitenFehler: string; busy: boolean; onNeu: () => void; onClose: () => void;
}) {
  const [gross, setGross] = useState(false);
  useEffect(() => {
    const schliessen = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", schliessen);
    return () => window.removeEventListener("keydown", schliessen);
  }, [onClose]);
  return (
    <div className="invoice-preview-overlay" role="dialog" aria-modal="true">
      <div className="invoice-preview-bar">
        <div><p className="invoice-kicker">Originalvorlage</p><h2>PDF-Vorschau</h2></div>
        <span className="invoice-preview-bar-actions">
          <button className="invoice-button" type="button" onClick={onNeu} disabled={busy}>
            {busy ? "Erzeugt …" : "↻ Neu erzeugen"}
          </button>
          {seiten.length > 0 && (
            <button className="invoice-button" type="button" aria-pressed={gross} onClick={() => setGross((g) => !g)}>
              {gross ? "Einpassen" : "Vergrößern"}
            </button>
          )}
          {url && <a className="invoice-button" href={url} target="_blank" rel="noreferrer">PDF</a>}
          <button className="invoice-button" type="button" onClick={onClose}>Schließen</button>
        </span>
      </div>
      {url && (seiten.length > 0 || seitenFehler) ? (
        <Seitenblatt
          quellen={seiten}
          titel="PDF-Vorschau"
          gross={gross}
          onGross={() => setGross((g) => !g)}
          fehler={!!seitenFehler}
          ersatz={<a href={url} target="_blank" rel="noreferrer">Als PDF öffnen</a>}
        />
      ) : (
        <div className="invoice-preview-empty">
          <span>PDF</span>
          <strong>{busy ? "Wird erzeugt …" : "Noch keine Vorschau"}</strong>
          <p>Nach Eingabe der Pflichtfelder „PDF-Vorschau“ wählen.</p>
        </div>
      )}
    </div>
  );
}

/**
 * Rechnungsanschrift in Einzelfeldern — und der Rückweg in die Kundendaten.
 *
 * Vorher stand hier ein Textfeld. Wer die Adresse darin korrigierte, korrigierte
 * sie nur für diesen einen Beleg: der Kunde blieb falsch, und beim nächsten Mal
 * stand derselbe Fehler wieder da. Weicht die Anschrift jetzt vom Kunden ab,
 * bietet die Maske an, ihn nachzuziehen — auf Nachfrage, nicht von allein.
 */
function AnschriftFelder({ anschrift, onChange, kunde }: {
  anschrift: Anschrift;
  onChange: (a: Anschrift) => void;
  kunde?: Kunde;
}) {
  const updateKunde = useUpdateKunde();
  const [gefragt, setGefragt] = useState(false);
  const [uebernommen, setUebernommen] = useState(false);
  const setzen = (feld: keyof Anschrift, wert: string) => {
    setUebernommen(false);
    onChange({ ...anschrift, [feld]: wert });
  };

  const ausKunde = kunde ? anschriftAusKunde(kunde) : null;
  const weichtAb = Boolean(ausKunde) && (
    ausKunde!.strasse !== anschrift.strasse.trim()
    || ausKunde!.plz !== anschrift.plz.trim()
    || ausKunde!.ort !== anschrift.ort.trim()
  );

  return (
    <>
      <label className="invoice-editor-field"><span>Empfänger *</span>
        <input value={anschrift.name} onChange={(e) => setzen("name", e.target.value)} placeholder="Name" />
      </label>
      <label className="invoice-editor-field"><span>Straße und Hausnummer</span>
        {/* Ein Vorschlag setzt Straße, PLZ und Ort in EINEM Schritt — drei
            einzelne `setzen` hintereinander überschrieben sich gegenseitig. */}
        <StrassenFeld
          value={anschrift.strasse}
          onChange={(v) => setzen("strasse", v)}
          onPick={(t) => {
            setUebernommen(false);
            onChange({ ...anschrift, strasse: t.strasse || anschrift.strasse, plz: t.plz || anschrift.plz, ort: t.ort || anschrift.ort });
          }}
          placeholder="Musterweg 12"
        />
      </label>
      <div className="invoice-editor-plz-ort">
        <label className="invoice-editor-field"><span>PLZ</span>
          <input value={anschrift.plz} onChange={(e) => setzen("plz", e.target.value)} placeholder="01099" inputMode="numeric" />
        </label>
        <label className="invoice-editor-field"><span>Ort *</span>
          <input value={anschrift.ort} onChange={(e) => setzen("ort", e.target.value)} placeholder="Dresden" />
        </label>
      </div>
      {uebernommen && <p className="invoice-anschrift-hinweis is-ok">In den Kundendaten gespeichert.</p>}
      {weichtAb && !uebernommen && kunde && (
        gefragt ? (
          <div className="invoice-anschrift-frage">
            <span>Rechnungsanschrift von <strong>{kunde.name}</strong> dauerhaft überschreiben?</span>
            <span className="invoice-anschrift-aktionen">
              <button
                type="button"
                className="invoice-button"
                disabled={updateKunde.isPending}
                onClick={() => updateKunde.mutate(
                  { id: kunde.id, patch: { rechnung_strasse: anschrift.strasse.trim(), rechnung_plz: anschrift.plz.trim(), rechnung_ort: anschrift.ort.trim() } },
                  { onSuccess: () => { setUebernommen(true); setGefragt(false); } },
                )}
              >
                {updateKunde.isPending ? "Speichert …" : "Ja, übernehmen"}
              </button>
              <button type="button" className="invoice-button secondary" onClick={() => setGefragt(false)}>Nur für diesen Beleg</button>
            </span>
          </div>
        ) : (
          <p className="invoice-anschrift-hinweis">
            Weicht von den Kundendaten ab.{" "}
            <button type="button" className="invoice-linkbutton" onClick={() => setGefragt(true)}>Beim Kunden übernehmen?</button>
          </p>
        )
      )}
    </>
  );
}

/**
 * Der Titel des fertigen Dokuments — eine Zeile, ganz vom Nutzer bestimmt.
 *
 * Steht in derselben Sektion wie das Anschreiben, weil beides zusammen den
 * Kopf des Belegs macht (Benjamin, 07.09.2026). Der Vorschlag füllt das Feld
 * vor, ist aber nur ein Vorschlag: jedes Zeichen lässt sich ändern, und die
 * Belegnummer steht bewusst nicht darin — sie erscheint im Metablock des PDFs
 * und würde einen selbst geschriebenen Titel veralten lassen, sobald sie sich
 * beim Festschreiben ändert.
 */
function TitelZeile({ draft, onChange }: {
  draft: WebInvoiceDraft;
  onChange: (titel: string) => void;
}) {
  const vorschlag = titelVorschlag(draft.kind, "");
  return (
    <>
      <div className="invoice-titel-zeile">
        <span className="invoice-titel-label">Titel des Dokuments</span>
        <input
          className="invoice-titel-feld"
          value={draft.doc_title || ""}
          onChange={(event) => onChange(event.target.value)}
          placeholder={vorschlag}
          aria-label="Titel des Dokuments"
          maxLength={500}
        />
        {(draft.doc_title || "").trim() !== vorschlag && (
          <button
            type="button"
            className="invoice-button invoice-button-quiet"
            onClick={() => onChange(vorschlag)}
          >
            Auf „{vorschlag}“ zurücksetzen
          </button>
        )}
      </div>
      <p className="invoice-editor-hint">
        So steht die Zeile im Dokument. Die Belegnummer kommt eigenständig oben
        rechts dazu und gehört nicht in den Titel.
      </p>
    </>
  );
}

/**
 * Text-Karte: Klappkarte hier, Bausteinlogik in `InvoiceTextKarte`.
 *
 * Die Trennung ist keine Willkür — die `Klappkarte` gehört dieser Seite und
 * wird von mehreren Karten benutzt, das Baustein-Verhalten dagegen nur hier.
 * Der Karteninhalt kommt deshalb als Funktion zurück, samt der Zeile für den
 * zugeklappten Kopf.
 */
function TextAreaCard({ liste, title, kicker, value, snippets, onChange, draft, standardOffen, vorspann, zusammenfassung }: {
  liste: SnippetListe;
  title: string;
  kicker: string;
  value: string;
  snippets: TextSnippet[];
  onChange: (value: string) => void;
  draft: WebInvoiceDraft;
  standardOffen?: boolean;
  vorspann?: React.ReactNode;
  zusammenfassung?: string;
}) {
  return (
    <InvoiceTextKarte
      liste={liste}
      titel={title}
      vorspann={vorspann}
      zusammenfassung={zusammenfassung}
      value={value}
      snippets={snippets}
      onChange={onChange}
      draft={draft}
    >
      {(inhalt, zusammenfassung) => (
        <Klappkarte kicker={kicker} titel={title} zusammenfassung={zusammenfassung} standardOffen={standardOffen}>
          {inhalt}
        </Klappkarte>
      )}
    </InvoiceTextKarte>
  );
}

/**
 * Karte, die sich zuklappen lässt — mit einer Zeile Zusammenfassung im Kopf.
 *
 * Empfänger und Anschreiben stellt man einmal ein; an den Positionen arbeitet
 * man immer wieder. Beim Wiederaufnehmen eines gespeicherten Entwurfs sind die
 * Einmal-Karten deshalb zu und die Positionen sofort im Blick. Ein neuer Beleg
 * startet offen, weil dort erst alles ausgefüllt werden muss.
 */
function Klappkarte({ kicker, titel, rechts, zusammenfassung, standardOffen = true, children }: {
  kicker: string;
  titel: string;
  rechts?: string;
  zusammenfassung?: string;
  standardOffen?: boolean;
  children: React.ReactNode;
}) {
  const [offen, setOffen] = useState(standardOffen);
  return (
    <section className={`invoice-editor-card${offen ? "" : " is-zu"}`}>
      <button type="button" className="invoice-editor-card-head is-klappbar" onClick={() => setOffen((v) => !v)}>
        <div>
          <p className="invoice-kicker">{kicker}</p>
          <h2>{titel}</h2>
          {!offen && zusammenfassung && <p className="invoice-card-summary">{zusammenfassung}</p>}
        </div>
        <span className="invoice-card-toggle">
          {rechts && offen ? <em>{rechts}</em> : null}
          <span aria-hidden="true" data-offen={offen}>▾</span>
        </span>
      </button>
      {offen && children}
    </section>
  );
}

function FinalizeDialog({ draft, total, busy, onClose, onConfirm, onKind, onZugferd }: { draft: WebInvoiceDraft; total: number; busy: boolean; onClose: () => void; onConfirm: () => void; onKind: (kind: InvoiceKind) => void; onZugferd: (an: boolean) => void }) {
  const { data: wert } = useProjektAuftragswert(draft.source_project_id || undefined);
  /* Nach Abschlägen ist die abschließende Rechnung eine Schlussrechnung (sie setzt
     die Abschläge ab, § 14 Abs. 5 UStG) — das muss vor dem Festschreiben feststehen,
     weil es auf dem Papier steht. Ob ein Rest aus dem Angebot entfällt, fragt
     dagegen erst der Fertig-Dialog (FestschreibenFolgen, Benjamin 15.10.2026):
     dort bleibt die Rechnung eine Rechnung und bekommt nur einen Vermerk. */
  const fragen = draft.kind === "invoice" && Boolean(wert) && (wert!.anzahl_abschlaege ?? 0) > 0;
  return (
    <div className="invoice-modal-backdrop">
      <section className="invoice-final-dialog" role="dialog" aria-modal="true">
        <p className="invoice-kicker">Verbindlicher Schritt</p>
        <h2>{belegArtLabel(draft.kind, draft.source_quote_number)} festschreiben?</h2>
        <p>Die nächste freie Nummer wird jetzt endgültig vergeben und das PDF im gemeinsamen Register gespeichert.</p>
        <dl>
          <div><dt>Kunde</dt><dd>{draft.customer_lines.split("\n")[0] || "—"}</dd></div>
          <div><dt>Voraussichtliche Nr.</dt><dd>{draft.doc_number || "automatisch"}</dd></div>
          <div><dt>Gesamtbetrag</dt><dd>{euro(total)}</dd></div>
        </dl>
        {/* Die E-Rechnung gehört zum Festschreiben — deshalb steht die Wahl hier
            und nicht mehr als eigene Karte im Editor. Angebote haben keine. */}
        {draft.kind !== "quote" && (
          <label className="invoice-editor-check">
            <input type="checkbox" checked={draft.zugferd} onChange={(event) => onZugferd(event.target.checked)} disabled={busy} />
            <span>
              <strong>Als E-Rechnung (ZUGFeRD/Factur-X)</strong>
              <small>Die strukturierten Rechnungsdaten werden in das PDF eingebettet.</small>
            </span>
          </label>
        )}
        {fragen && (
          <div className="invoice-abschluss-frage">
            <strong>Ist das die Schlussrechnung?</strong>
            <p>
              Für dieses Projekt wurden bereits {wert!.anzahl_abschlaege} Abschlagsrechnung(en) über
              zusammen {euro(wert!.gestellt)} netto gestellt. Die Schlussrechnung setzt sie ab und legt
              den Auftragswert auf die Summe aller Rechnungen fest; eine weitere Rechnung lässt ihn offen.
            </p>
            <div className="invoice-abschluss-optionen">
              <button type="button" className="invoice-button" onClick={onClose}>
                Weitere Rechnung — so lassen
              </button>
              <button type="button" className="invoice-button primary" onClick={() => onKind("final_invoice")}>
                Ja, als Schlussrechnung festschreiben
              </button>
            </div>
          </div>
        )}
        <div className="invoice-modal-actions">
          <button className="invoice-button" type="button" onClick={onClose} disabled={busy}>Zurück</button>
          <button className="invoice-button primary" type="button" onClick={onConfirm} disabled={busy}>{busy ? "Wird festgeschrieben …" : "Jetzt festschreiben"}</button>
        </div>
      </section>
    </div>
  );
}

function FinalizedDialog({ result, onRegister, onBearbeiten }: { result: InvoiceFinalizeResult; onRegister: () => void; onBearbeiten: () => void }) {
  const [dok, setDok] = useState<Dokument>(null);
  return (
    <div className="invoice-modal-backdrop">
      <section className="invoice-final-dialog success" role="dialog" aria-modal="true">
        <span className="invoice-final-check">✓</span>
        <p className="invoice-kicker">Fertig</p>
        <h2>{result.number} wurde erstellt</h2>
        <p>Das Dokument ist festgeschrieben und im Register verfügbar.</p>
        {(result.projekt_wirkung || result.folgen) && (
          <FestschreibenFolgen wirkung={result.projekt_wirkung} folgen={result.folgen} item={result.item} />
        )}
        <Versand item={result.item} />
        <div className="invoice-modal-actions">
          <button className="invoice-button" type="button" onClick={onRegister}>Zum Register</button>
          {/* Beim Ansehen fällt noch etwas auf? Vor dem Versand zurück in den
              Editor — die Nummer bleibt, PDF und XML entstehen neu
              (Benjamin, 25.09.2026). */}
          <button className="invoice-button" type="button" onClick={onBearbeiten} title="Den festgeschriebenen Beleg ändern — die Nummer bleibt">Bearbeiten</button>
          <button className="invoice-button primary" type="button" onClick={() => setDok(invoiceDokument(result.item))}>Ansehen</button>
        </div>
      </section>
      <DokumentLightbox dok={dok} onClose={() => setDok(null)} />
    </div>
  );
}

/**
 * Beleg direkt aus dem eigenen Postfach verschicken.
 *
 * Festschreiben und Verschicken gehören zusammen — wer den Dialog schloss,
 * musste den Beleg im Register wiederfinden und von Hand anhängen. Empfänger,
 * Betreff und Text sind vorbelegt und bleiben änderbar; das Anschreiben kommt
 * aus der Vorlage des Inhabers. Dasselbe Formular hängt im Beleg-Drawer der
 * Übersicht — siehe `components/invoices/BelegVersand.tsx`.
 */
function Versand({ item }: { item: InvoiceRegisterItem }) {
  const { data: kunden = [] } = useKunden();
  const kunde = kunden.find((k) => k.id === item.kunde_id);
  return (
    <BelegVersand
      item={item}
      kundeEmail={kunde?.email || ""}
      kundeKontakteJson={kunde?.kontakte_json || ""}
      kundeName={kunde?.name || item.kunde_name}
      kunde={kunde}
      anstoss="✉ Jetzt an den Kunden schicken"
    />
  );
}

